import { useState, useEffect } from 'react';

export const useCountdown = (targetDate) => {
  const calculateTimeLeft = () => {
    if (!targetDate) return null;
    
    const target = new Date(targetDate);
    const now = new Date();
    const difference = target - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      expired: false,
      total: difference
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

export const CountdownTimer = ({ endDate, size = 'default', showLabels = true }) => {
  const timeLeft = useCountdown(endDate);

  if (!timeLeft) return null;

  const { days, hours, minutes, seconds, expired } = timeLeft;

  if (expired) {
    return (
      <div className="countdown-expired text-red-500 font-semibold">
        Auction Ended
      </div>
    );
  }

  const isUrgent = days === 0 && hours < 2;

  const sizeClasses = {
    small: 'text-xs',
    default: 'text-sm',
    large: 'text-lg'
  };

  const valueClasses = {
    small: 'text-base font-bold',
    default: 'text-xl font-bold',
    large: 'text-3xl font-bold'
  };

  return (
    <div className={`countdown-timer ${isUrgent ? 'urgent' : ''}`} data-testid="countdown-timer">
      <div className={`flex items-center gap-1 ${sizeClasses[size]} ${isUrgent ? 'text-red-500' : 'text-foreground'}`}>
        {days > 0 && (
          <div className="flex flex-col items-center min-w-[40px]">
            <span className={`${valueClasses[size]} tabular-nums ${isUrgent ? 'text-red-500' : 'text-foreground'}`}>
              {String(days).padStart(2, '0')}
            </span>
            {showLabels && <span className="uppercase tracking-wider text-slate-600">days</span>}
          </div>
        )}
        {days > 0 && <span className={`${valueClasses[size]} ${isUrgent ? 'text-red-500' : 'text-foreground'}`}>:</span>}
        <div className="flex flex-col items-center min-w-[40px]">
          <span className={`${valueClasses[size]} tabular-nums ${isUrgent ? 'text-red-500' : 'text-foreground'}`}>
            {String(hours).padStart(2, '0')}
          </span>
          {showLabels && <span className="uppercase tracking-wider text-slate-600">hrs</span>}
        </div>
        <span className={`${valueClasses[size]} ${isUrgent ? 'text-red-500' : 'text-foreground'}`}>:</span>
        <div className="flex flex-col items-center min-w-[40px]">
          <span className={`${valueClasses[size]} tabular-nums ${isUrgent ? 'text-red-500' : 'text-foreground'}`}>
            {String(minutes).padStart(2, '0')}
          </span>
          {showLabels && <span className="uppercase tracking-wider text-slate-600">min</span>}
        </div>
        <span className={`${valueClasses[size]} ${isUrgent ? 'text-red-500' : 'text-foreground'}`}>:</span>
        <div className="flex flex-col items-center min-w-[40px]">
          <span className={`${valueClasses[size]} tabular-nums ${isUrgent ? 'text-red-500 animate-pulse' : 'text-foreground'}`}>
            {String(seconds).padStart(2, '0')}
          </span>
          {showLabels && <span className="uppercase tracking-wider text-slate-600">sec</span>}
        </div>
      </div>
    </div>
  );
};

export const CompactCountdown = ({ endDate }) => {
  const timeLeft = useCountdown(endDate);

  if (!timeLeft) return null;

  const { days, hours, minutes, seconds, expired } = timeLeft;

  if (expired) {
    return <span className="text-red-500 text-xs font-medium">Ended</span>;
  }

  const isUrgent = days === 0 && hours < 2;

  if (days > 0) {
    return (
      <span className={`text-xs font-medium ${isUrgent ? 'text-red-500' : 'text-slate-800'}`}>
        {days}d {hours}h left
      </span>
    );
  }

  return (
    <span className={`text-xs font-medium tabular-nums ${isUrgent ? 'text-red-500 animate-pulse' : 'text-slate-800'}`}>
      {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
    </span>
  );
};

export default CountdownTimer;
